/**
 * React hook for managing language selection and changes.
 * Provides type-safe language state and change handler with error handling.
 * Supports optional auto-sync to user profile.
 */

import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isLanguage, type Language } from '../i18n/types';
import { api } from '../api/client';

/**
 * Options for the useLanguage hook.
 */
interface UseLanguageOptions {
  /**
   * Whether to sync language changes to the user's profile.
   * When true, language changes will update the preferred_language field in the backend.
   * @default false
   */
  syncToProfile?: boolean;

  /**
   * Debounce delay in milliseconds for profile sync.
   * Prevents rapid API calls when user switches languages quickly.
   * @default 500
   */
  debounceMs?: number;

  /**
   * Callback for sync errors.
   * Called when the profile sync fails (the UI language change still succeeds).
   */
  onSyncError?: (error: Error) => void;
}

/**
 * Return type for the useLanguage hook.
 */
interface UseLanguageReturn {
  /**
   * Current active language.
   */
  language: Language;

  /**
   * Function to change the language.
   * Returns a promise that resolves when language change is complete.
   *
   * @param newLanguage - The language to switch to
   * @throws Error if language change fails
   */
  changeLanguage: (newLanguage: Language) => Promise<void>;

  /**
   * Loading state during language change.
   * True while translations are being loaded.
   */
  isChanging: boolean;

  /**
   * Whether a profile sync is in progress.
   * True while waiting for the API call to complete.
   */
  isSyncing: boolean;
}

/**
 * Custom hook for language management.
 * Wraps react-i18next's useTranslation with type-safe language handling.
 * Optionally syncs language changes to the user's profile in the backend.
 *
 * @param options - Configuration options for the hook
 * @returns Object containing current language, change handler, and loading states
 *
 * @example
 * ```typescript
 * // Basic usage without profile sync
 * function LanguageSwitcher() {
 *   const { language, changeLanguage, isChanging } = useLanguage();
 *
 *   return (
 *     <button onClick={() => changeLanguage('nl')} disabled={isChanging}>
 *       Nederlands
 *     </button>
 *   );
 * }
 *
 * // With profile sync (for authenticated users)
 * function AuthenticatedLanguageSwitcher() {
 *   const { language, changeLanguage, isChanging, isSyncing } = useLanguage({
 *     syncToProfile: true,
 *     onSyncError: (error) => console.error('Sync failed:', error),
 *   });
 *
 *   return (
 *     <button onClick={() => changeLanguage('fr')} disabled={isChanging}>
 *       Français {isSyncing && '(saving...)'}
 *     </button>
 *   );
 * }
 * ```
 */
export const useLanguage = (options: UseLanguageOptions = {}): UseLanguageReturn => {
  const { syncToProfile = false, debounceMs = 500, onSyncError } = options;
  const { i18n } = useTranslation();
  const [isChanging, setIsChanging] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Refs for debouncing profile sync
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pendingLanguageRef = useRef<Language | null>(null);

  /**
   * Sync language to user profile via API.
   * This is called after a debounce delay to prevent rapid API calls.
   */
  const syncToProfileApi = useCallback(
    async (language: Language): Promise<void> => {
      setIsSyncing(true);
      try {
        await api.patch('/users/me/profile', { preferred_language: language });
      } catch (error) {
        const syncError =
          error instanceof Error ? error : new Error('Failed to sync language to profile');
        onSyncError?.(syncError);
      } finally {
        setIsSyncing(false);
      }
    },
    [onSyncError],
  );

  /**
   * Memoized language change handler.
   * Changes UI language immediately, then syncs to profile if enabled.
   */
  const changeLanguage = useCallback(
    async (newLanguage: Language): Promise<void> => {
      // Validate language
      if (!isLanguage(newLanguage)) {
        throw new Error(`Invalid language: ${newLanguage}`);
      }

      // Skip if already the current language
      if (i18n.language === newLanguage) {
        return;
      }

      setIsChanging(true);

      try {
        // Change language using i18next (immediate UI update)
        await i18n.changeLanguage(newLanguage);

        // Explicitly persist the language choice to localStorage.
        // i18next should call customLanguageDetector.cacheUserLanguage() which
        // sets 'i18n_language', but we write it directly as a defensive fallback
        // to keep the key up to date when possible.
        try {
          localStorage.setItem('i18n_language', newLanguage);
        } catch {
          // Silently ignore localStorage errors
        }

        // Update localStorage session with new language for persistence
        try {
          const sessionStr = localStorage.getItem('session');
          if (sessionStr) {
            const session = JSON.parse(sessionStr);
            session.preferredLanguage = newLanguage;
            localStorage.setItem('session', JSON.stringify(session));
          }
        } catch {
          // Silently ignore localStorage errors
        }

        // Schedule profile sync if enabled (debounced)
        if (syncToProfile) {
          // Clear any pending sync
          if (syncTimeoutRef.current) {
            clearTimeout(syncTimeoutRef.current);
          }

          // Track the pending language
          pendingLanguageRef.current = newLanguage;

          // Schedule the sync after debounce delay
          syncTimeoutRef.current = setTimeout(() => {
            if (pendingLanguageRef.current === newLanguage) {
              syncToProfileApi(newLanguage);
              pendingLanguageRef.current = null;
            }
          }, debounceMs);
        }
      } catch (error) {
        // Re-throw with more context
        const message =
          error instanceof Error ? error.message : 'Unknown error';
        throw new Error(`Failed to change language to ${newLanguage}: ${message}`, { cause: error });
      } finally {
        setIsChanging(false);
      }
    },
    [i18n, syncToProfile, debounceMs, syncToProfileApi],
  );

  // Extract current language with type safety
  const currentLanguage: Language = isLanguage(i18n.language)
    ? i18n.language
    : 'en';

  return {
    language: currentLanguage,
    changeLanguage,
    isChanging,
    isSyncing,
  };
};
